import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MessageSquare, X } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import {
  BODY_MAX,
  CATEGORIES,
  CATEGORY_HINT,
  useMarkSeen,
  useMyFeedback,
  useSubmitFeedback,
  type Category,
  type Status,
} from "@/lib/feedback";
import { cn } from "@/lib/cn";

const STATUS_VARIANT: Record<Status, "neutral" | "primary" | "success" | "destructive"> =
  {
    New: "neutral",
    "In Progress": "primary",
    Resolved: "success",
    Declined: "destructive",
  };

function Submit({ onDone }: { onDone: () => void }) {
  const [category, setCategory] = useState<Category | "">("");
  const [body, setBody] = useState("");
  const submit = useSubmitFeedback();

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!category) return;
        submit.mutate({ category, body }, { onSuccess: onDone });
      }}
    >
      <div>
        <label htmlFor="fb-cat" className="mb-1.5 block text-sm font-medium">
          What kind of feedback? <span className="text-destructive">*</span>
        </label>
        {/* ⚠ Required by the shared pattern — the API rejects a submission
            without one, so this is not merely a UI nicety. */}
        <Select
          id="fb-cat"
          required
          value={category}
          onChange={(e) => setCategory(e.target.value as Category)}
          className="w-full"
        >
          <option value="" disabled>
            Choose one…
          </option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c} — {CATEGORY_HINT[c]}
            </option>
          ))}
        </Select>
      </div>

      <div>
        <label htmlFor="fb-body" className="mb-1.5 block text-sm font-medium">
          Message
        </label>
        <textarea
          id="fb-body"
          required
          rows={5}
          maxLength={BODY_MAX}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="What happened, or what would help?"
          className="border-input bg-card text-foreground placeholder:text-muted-foreground w-full rounded-md border px-3 py-2 text-sm"
        />
        {body.length > BODY_MAX - 500 && (
          <p className="text-muted-foreground mt-1 text-right text-xs">
            {body.length.toLocaleString()} / {BODY_MAX.toLocaleString()}
          </p>
        )}
      </div>

      {/* The form keeps what was typed on failure — it is only cleared once
          the item is safely saved. */}
      {submit.error && (
        <p className="text-destructive text-sm">
          {submit.error instanceof Error ? submit.error.message : "Could not send."}
        </p>
      )}

      <Button
        type="submit"
        size="sm"
        disabled={submit.isPending || !category || !body.trim()}
      >
        {submit.isPending ? "Sending…" : "Send feedback"}
      </Button>
    </form>
  );
}

function Mine({ justSent }: { justSent: boolean }) {
  const { data } = useMyFeedback();
  const markSeen = useMarkSeen();

  // Opening the list is the user seeing the update, so clear the flags now.
  useEffect(() => {
    for (const item of data?.feedback ?? []) {
      if (item.raiserUnseen) markSeen.mutate(item.id);
    }
    // Intentionally keyed on the data only; markSeen is stable enough and
    // re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const sent = justSent && (
    <p className="bg-accent text-accent-foreground mb-3 rounded-md px-3 py-2 text-sm">
      Thanks — sent to the admin team, with a copy to your email. Its status
      and any reply will show here.
    </p>
  );

  if (!data || data.feedback.length === 0) {
    return (
      <>
        {sent}
        <p className="text-muted-foreground py-6 text-center text-sm">
          You haven’t sent any feedback yet.
        </p>
      </>
    );
  }

  return (
    <>
      {sent}
      <ul className="space-y-3">
        {data.feedback.map((f) => (
          <li key={f.id} className="border-border rounded-lg border p-3">
            <div className="mb-1.5 flex items-center gap-2">
              <Badge variant="outline">{f.category}</Badge>
              <Badge variant={STATUS_VARIANT[f.status]}>{f.status}</Badge>
              {f.raiserUnseen && <Badge variant="warning">Updated</Badge>}
              <span className="text-muted-foreground ml-auto text-xs">
                {new Date(f.createdAt).toLocaleDateString()}
              </span>
            </div>
            <p className="text-sm whitespace-pre-wrap">{f.body}</p>
            {f.adminRemark && (
              <p className="bg-muted text-muted-foreground mt-2 rounded p-2 text-sm">
                <span className="font-medium">Reply:</span> {f.adminRemark}
              </p>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * The feedback entry point.
 *
 * ⚠ Sits directly beside the account area — the house pattern fixes that
 * adjacency even though the styling is free.
 */
export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"send" | "mine">("send");
  const [justSent, setJustSent] = useState(false);
  const { data } = useMyFeedback();
  const unseen = data?.unseen ?? 0;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    // Without this the page behind scrolls under the dialog on a phone, so a
    // stray swipe on the backdrop moves the directory rather than the form.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setJustSent(false);
          setOpen(true);
        }}
        aria-label={unseen > 0 ? `Feedback (${unseen} updated)` : "Feedback"}
        className="text-muted-foreground hover:text-foreground hover:bg-accent relative grid h-8 w-8 place-items-center rounded-md"
      >
        <MessageSquare size={16} />
        {unseen > 0 && (
          <span className="bg-destructive absolute top-1 right-1 h-2 w-2 rounded-full" />
        )}
      </button>

      {/*
        ⚠ Portalled to <body>, not rendered where it sits.

        This button lives in the header, and the header has `backdrop-blur` —
        a backdrop-filter makes an element a containing block for *fixed*
        descendants. So `fixed inset-0` resolved against the header rather
        than the viewport, and the dialog was laid out inside a 60px-tall box:
        on a phone its title, tabs and close button ended up off-screen with
        no way to scroll to them. Escaping the header fixes it at the root.
      */}
      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/40 p-4"
            onClick={() => setOpen(false)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Feedback"
              onClick={(e) => e.stopPropagation()}
              className="bg-card border-border max-h-[85dvh] w-full max-w-lg overflow-y-auto rounded-lg border p-5 shadow-lg"
            >
              <div className="mb-4 flex items-center gap-2">
                <h2 className="font-semibold">Feedback</h2>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="hover:bg-accent ml-auto -m-1 rounded p-1"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="border-border mb-4 flex gap-1 border-b">
                {(["send", "mine"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setJustSent(false);
                      setTab(t);
                    }}
                    className={cn(
                      "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                      tab === t
                        ? "border-primary text-foreground"
                        : "text-muted-foreground hover:text-foreground border-transparent",
                    )}
                  >
                    {t === "send" ? "Send" : `Mine${unseen > 0 ? ` (${unseen})` : ""}`}
                  </button>
                ))}
              </div>

              {tab === "send" ? (
                <Submit
                  onDone={() => {
                    setJustSent(true);
                    setTab("mine");
                  }}
                />
              ) : (
                <Mine justSent={justSent} />
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
